import { isDefined } from '../guards'
import type { ICanvasSize, Nullable, Property, UpdateValue, VertexAttribute } from '../types'
import { getKeyValuePairs } from './forTypes'

export const requestRedraw = (setUpdate: UpdateValue<number>) => {
  setUpdate(value => value + 1)
}

export const getWebGlContext = (canvas: HTMLCanvasElement): WebGL2RenderingContext => {
  const renderingContext = canvas.getContext('webgl2')
  if (!isDefined(renderingContext)) {
    throw new Error('renderingContext is not defined')
  }
  return renderingContext
}

export const setCanvasSize = (
  canvas: HTMLCanvasElement,
  canvasWidth: number,
  canvasHeight: number,
  realToCssPixels: number,
) => {
  canvas.width = Math.floor(canvasWidth * realToCssPixels)
  canvas.height = Math.floor(canvasHeight * realToCssPixels)
}

export const isCanvasSizeNotZero = (canvas: ICanvasSize) =>
  canvas.height !== 0 && canvas.width !== 0 && canvas.offsetHeight !== 0 && canvas.offsetWidth !== 0

export function setupWebGl(renderingContext: WebGL2RenderingContext) {
  renderingContext.viewport(
    0,
    0,
    renderingContext.drawingBufferWidth,
    renderingContext.drawingBufferHeight,
  )
  renderingContext.clearColor(0, 0, 0, 0)
  renderingContext.clear(renderingContext.COLOR_BUFFER_BIT | renderingContext.DEPTH_BUFFER_BIT)
}

function compileShader(
  renderingContext: WebGL2RenderingContext,
  shaderSource: string,
  shaderType: GLenum,
): WebGLShader {
  const shader = renderingContext.createShader(shaderType)
  if (!isDefined(shader)) {
    throw new Error('Shader is not created')
  }

  renderingContext.shaderSource(shader, shaderSource)
  renderingContext.compileShader(shader)

  const isCompiled = renderingContext.getShaderParameter(shader, renderingContext.COMPILE_STATUS)
  if (!isCompiled) {
    const lastError = renderingContext.getShaderInfoLog(shader)
    renderingContext.deleteShader(shader)
    throw new Error(`Error compiling shader: ${lastError}}`)
  }

  return shader
}

export function getOrCreateProgram(
  programs: Property<Record<string, WebGLProgram>>,
  renderingContext: WebGL2RenderingContext,
  vertexShaderSource: string,
  fragmentShaderSource: string,
): WebGLProgram {
  const index = `${vertexShaderSource},${fragmentShaderSource}`
  if (index in programs.value) return programs.value[index]

  const program = createProgram(renderingContext, vertexShaderSource, fragmentShaderSource)
  programs.setValue({ ...programs.value, [index]: program })
  return program
}

export function createProgram(
  renderingContext: WebGL2RenderingContext,
  vertexShaderSource: string,
  fragmentShaderSource: string,
): WebGLProgram {
  const vertexShader = compileShader(
    renderingContext,
    vertexShaderSource,
    renderingContext.VERTEX_SHADER,
  )
  const fragmentShader = compileShader(
    renderingContext,
    fragmentShaderSource,
    renderingContext.FRAGMENT_SHADER,
  )

  const program = renderingContext.createProgram()
  if (!isDefined(program)) {
    throw new Error('program is not defined')
  }

  renderingContext.attachShader(program, vertexShader)
  renderingContext.attachShader(program, fragmentShader)
  renderingContext.linkProgram(program)

  const isLinked = renderingContext.getProgramParameter(program, renderingContext.LINK_STATUS)
  if (!isLinked) {
    const lastError = renderingContext.getProgramInfoLog(program)
    renderingContext.deleteProgram(program)
    throw new Error(isDefined(lastError) ? lastError : 'program did not link')
  }

  renderingContext.detachShader(program, vertexShader)
  renderingContext.deleteShader(vertexShader)
  renderingContext.detachShader(program, fragmentShader)
  renderingContext.deleteShader(fragmentShader)

  return program
}

export const createAttribute = (
  renderingContext: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
  size: number,
  type: number,
  normalize = false,
): VertexAttribute => {
  return {
    index: renderingContext.getAttribLocation(program, name),
    buffer: null,
    size,
    type,
    normalize,
  }
}

const deleteAttribute = (renderingContext: WebGL2RenderingContext, attribute: VertexAttribute) => {
  renderingContext.deleteBuffer(attribute.buffer)
}

export const bindAttribute = (
  renderingContext: WebGL2RenderingContext,
  attribute: VertexAttribute,
) => {
  renderingContext.bindBuffer(renderingContext.ARRAY_BUFFER, attribute.buffer)
  renderingContext.enableVertexAttribArray(attribute.index)
  renderingContext.vertexAttribPointer(
    attribute.index,
    attribute.size,
    attribute.type,
    attribute.normalize,
    0,
    0,
  )
}

export const unbindAttribute = (
  renderingContext: WebGL2RenderingContext,
  attribute: VertexAttribute,
) => {
  renderingContext.disableVertexAttribArray(attribute.index)
  renderingContext.bindBuffer(renderingContext.ARRAY_BUFFER, null)
}

const mapWithRenderingContext = <T extends string, V>(
  renderingContext: WebGL2RenderingContext,
  records: Record<T, V>,
  func: (renderingContext: WebGL2RenderingContext, value: V) => void,
) => {
  getKeyValuePairs(records).forEach(({ value }) => {
    func(renderingContext, value)
  })
}

export const bindAttributes = <T extends string>(
  renderingContext: WebGL2RenderingContext,
  attributes: Record<T, VertexAttribute>,
) => {
  mapWithRenderingContext(renderingContext, attributes, bindAttribute)
}

export const unbindAttributes = <T extends string>(
  renderingContext: WebGL2RenderingContext,
  attributes: Record<T, VertexAttribute>,
) => {
  mapWithRenderingContext(renderingContext, attributes, unbindAttribute)
}

export const deleteAttributes = <T extends string>(
  renderingContext: WebGL2RenderingContext,
  attributes: Record<T, VertexAttribute>,
) => {
  mapWithRenderingContext(renderingContext, attributes, deleteAttribute)
}

export const createIndexBufferUint16 = (
  renderingContext: WebGL2RenderingContext,
  indices: number[],
) => {
  const indexBuffer = renderingContext.createBuffer()
  renderingContext.bindBuffer(renderingContext.ELEMENT_ARRAY_BUFFER, indexBuffer)
  renderingContext.bufferData(
    renderingContext.ELEMENT_ARRAY_BUFFER,
    new Uint16Array(indices),
    renderingContext.STATIC_DRAW,
  )
  return indexBuffer
}

export const createIndexBufferUint32 = (
  renderingContext: WebGL2RenderingContext,
  indices: number[] | Uint32Array,
) => {
  const indexBuffer = renderingContext.createBuffer()
  renderingContext.bindBuffer(renderingContext.ELEMENT_ARRAY_BUFFER, indexBuffer)
  renderingContext.bufferData(
    renderingContext.ELEMENT_ARRAY_BUFFER,
    new Uint32Array(indices),
    renderingContext.STATIC_DRAW,
  )
  return indexBuffer
}

export const bindIndexBuffer = (
  renderingContext: WebGL2RenderingContext,
  buffer: Nullable<WebGLBuffer>,
) => {
  renderingContext.bindBuffer(renderingContext.ELEMENT_ARRAY_BUFFER, buffer)
}

const createAttributeBuffer = (
  renderingContext: WebGL2RenderingContext,
  sourceBuffer: ArrayBufferView,
  target?: number,
  usage?: number,
) => {
  const buffer = renderingContext.createBuffer()
  renderingContext.bindBuffer(renderingContext.ARRAY_BUFFER, buffer)
  renderingContext.bufferData(
    target ?? renderingContext.ARRAY_BUFFER,
    sourceBuffer,
    usage ?? renderingContext.STATIC_DRAW,
    0,
  )
  return buffer
}

export const updateAttributeBuffer = (
  renderingContext: WebGL2RenderingContext,
  attribute: VertexAttribute,
  sourceBuffer: ArrayBufferView,
) => {
  if (attribute.buffer) {
    renderingContext.deleteBuffer(attribute.buffer)
  }

  attribute.buffer = createAttributeBuffer(renderingContext, sourceBuffer)
}

export function getOrCreateTexture(
  textures: Property<Record<string, WebGLTexture>>,
  textureName: string,
  createTexture: () => WebGLTexture,
): WebGLTexture {
  if (textureName in textures.value) return textures.value[textureName]

  const texture = createTexture()
  textures.setValue({ ...textures.value, [textureName]: texture })
  return texture
}
